import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { Settings, User, Shield, Bell } from "lucide-react";
import { Button } from "../components/ui/button";

export default function SettingsPage() {
  const { user } = useAuth();

  return (
    <div className="p-6 sm:p-8" data-testid="settings-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your account and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Profile */}
        <div className="lg:col-span-2 space-y-6">
          <div className="enterprise-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <User className="w-5 h-5 text-slate-400" />
              <h2 className="text-lg font-medium text-slate-900">Profile</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
                <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-medium text-slate-600">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div>
                  <p className="text-lg font-medium text-slate-900">{user?.name}</p>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 mb-1">Role</p>
                  <p className="text-sm text-slate-900 capitalize">{user?.role}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 mb-1">User ID</p>
                  <p className="text-sm text-slate-900 font-mono">{user?.id}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="enterprise-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-5 h-5 text-slate-400" />
              <h2 className="text-lg font-medium text-slate-900">Security</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-slate-200">
                <div>
                  <p className="text-sm font-medium text-slate-900">Password</p>
                  <p className="text-xs text-slate-500">Last changed: Unknown</p>
                </div>
                <Button variant="outline" size="sm">
                  Change Password
                </Button>
              </div>
              
              <div className="flex items-center justify-between py-3 border-b border-slate-200">
                <div>
                  <p className="text-sm font-medium text-slate-900">Two-Factor Authentication</p>
                  <p className="text-xs text-slate-500">Add an extra layer of security</p>
                </div>
                <Button variant="outline" size="sm" disabled>
                  Coming Soon
                </Button>
              </div>
              
              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-slate-900">Active Sessions</p>
                  <p className="text-xs text-slate-500">Manage your logged-in devices</p>
                </div>
                <Button variant="outline" size="sm" disabled>
                  View Sessions
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="enterprise-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <Bell className="w-5 h-5 text-slate-400" />
              <h2 className="text-lg font-medium text-slate-900">Notifications</h2>
            </div>
            
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" className="rounded border-slate-300" defaultChecked disabled />
                <span className="text-sm text-slate-600">Email notifications</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" className="rounded border-slate-300" defaultChecked disabled />
                <span className="text-sm text-slate-600">Audit alerts</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" className="rounded border-slate-300" disabled />
                <span className="text-sm text-slate-600">System updates</span>
              </label>
            </div>
            
            <p className="text-xs text-slate-400 mt-4">Notification settings coming soon</p>
          </div>

          <div className="enterprise-card p-6">
            <h2 className="text-lg font-medium text-slate-900 mb-4">System Info</h2>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Version</span>
                <span className="text-slate-900">1.0.0</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Environment</span>
                <span className="text-slate-900">Production</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">API Status</span>
                <span className="text-green-600">Online</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
